<!-- src/views/management/permissions.vue -->
<script setup lang="ts">
import LogoutTime from '@/layouts/components/LogoutTime.vue'
import UserProfile from '@/layouts/components/UserProfile.vue'

import menusV2 from "@/datas/menus_v2"

import { useSnackBar } from '@/utils/stores/snackbar'

import { computed, onMounted, reactive, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"

// ▶ 페이지 메타 (blank 레이아웃 유지)
definePage({
  meta: {
    layout: 'blank',
    action: 'read',
    subject: 'permissions',
  },
})

// ---------- 메뉴 버전 (관리 페이지는 프로젝트에 종속되지 않으므로 menusV2 고정) ----------
const menus = computed(() => menusV2)


// ---------- 스낵바 ----------
const snackbarStore = useSnackBar()

// ---------- 라우팅 탭(관리 섹션) ----------
const route = useRoute()
const router = useRouter()
type MgmtTab = 'permissions' | 'users' | 'sites'
const mgmtTab = ref<MgmtTab>('permissions')

function syncTabFromRoute() {
  const path = route.path || ''
  if (path.includes('/manage/users')) mgmtTab.value = 'users'
  else if (path.includes('/manage/sites')) mgmtTab.value = 'sites'
  else mgmtTab.value = 'permissions'
}
onMounted(syncTabFromRoute)
watch(() => route.fullPath, syncTabFromRoute)
watch(mgmtTab, v => {
  const next =
    v === 'users' ? '/manage/users'
    : v === 'sites' ? '/manage/sites'
    : '/manage/permissions'
  if (route.path !== next) router.push(next)
})

// ---------- Roles & Permissions ----------
const roleData = ref<any[]>([])
const currentRole = ref<{ name: string } | null>(null)

// 선택된 Role의 퍼미션 객체( subject: [actions] )
const p = reactive<Record<string, string[]>>({})

// 전체 롤 가져오기
const { data: allRoles, error: rolesError } = await useApi(createUrl('/management/roles/all', { query: {} }))
if (rolesError.value) {
  // eslint-disable-next-line no-console
  console.error(rolesError.value)
} else {
  // 첫 번째(예: Super Admin) 제외하려던 의도 유지
  roleData.value = (allRoles.value || []).slice(1)
}

// Role 선택 시 서버에서 퍼미션 로드
async function getPermissions(role: { name: string }) {
  currentRole.value = role
  const { data, error } = await useApi(createUrl('/management/permissions/by-role', {
    query: { role: role.name },
  }))
  if (error.value) {
    // eslint-disable-next-line no-console
    console.error(error.value)
    return
  }
  // 객체 초기화 후 매핑
  Object.keys(p).forEach(k => delete p[k])
  Object.assign(p, vuexyPermissionToObject(data.value || []))
}

// 첫 로드 시 첫 Role 자동 선택
onMounted(() => {
  if (roleData.value.length) getPermissions(roleData.value[0])
})

// 서버 응답(vuexy 형태) → 화면 객체로
function vuexyPermissionToObject(permissions: Array<{ subject: string; action: string }>) {
  const out: Record<string, string[]> = {}
  for (const it of permissions) {
    if (out[it.subject]) out[it.subject].push(it.action)
    else out[it.subject] = [it.action]
  }
  return out
}

// 복수 권한 항목(예: "등록" → source-site + source-user)의 대표 키 반환
function permKey(menu: any): string {
  return menu.permission || (menu.permissions?.[0] ?? '')
}

// 복수 권한 항목 변경 시 모든 하위 권한에 동기화
function onPermChange(menu: any, value: string[]) {
  if (menu.permissions) {
    menu.permissions.forEach((perm: string) => { p[perm] = [...value] })
  } else {
    p[menu.permission] = value
  }
}

// 저장
async function save() {
  if (!currentRole.value?.name) return

  const flat: string[] = []
  Object.keys(p).forEach(subject => {
    const arr = Array.isArray(p[subject]) ? p[subject] : []
    arr.forEach(action => flat.push(`${subject}_${action}`))
  })

  await $api(`/management/permissions/sync-role`, {
    method: 'POST',
    body: {
      role: currentRole.value.name,
      permissions: flat,
    },
  })

  snackbarStore.showSnackBar("Successfully Saved!")
}

// ---------- 상단 ‘관리’ 버튼 hover ----------
const hover = ref(false)
</script>

<template>
  <!-- 상단 헤더 (Projects 화면에서 가져온 컴포넌트) -->
  <VCard class="topbar-card" style="border-radius:0;">
  <VCardText class="d-flex justify-space-between align-between py-2">
    <!-- 👇 로고 버튼 (왼쪽) -->
    <div
      class="d-flex align-center cursor-pointer"
      @click="router.push('/projects')"
    >
      <img
        src="/images/home_logo.png"
        alt="홈"
        width="87"
        height="30"
        style="display:block"
      />
    </div>

    <!-- 오른쪽 기존 요소 -->
    <div class="d-flex align-center">
      <LogoutTime class="me-2" />
      <UserProfile />
      <div class="divider-vert" />
      <div
        class="mgmt-quick d-flex align-center px-2 py-1"
        :style="{ backgroundColor: hover ? 'rgba(0,0,0,0.05)' : 'transparent' }"
        @click="router.push('/manage/permissions')"
        @mouseover="hover = true"
        @mouseleave="hover = false"
      >
        <img src="/images/settings.png" alt="관리" class="icon-20 me-1" />
        <span class="text-gray-600">관리</span>
      </div>
    </div>
  </VCardText>
</VCard>


  <!-- 페이지 타이틀 -->
  <div class="page-title-wrap">
    <h2 class="page-title">관리</h2>
  </div>

  <!-- 상단 관리 탭 -->
  <div class="mgmt-tabs">
    <VBtnToggle v-model="mgmtTab" class="tab-toggle" mandatory divided borderless>
      <VBtn :value="'permissions'" class="tab-btn">권한 관리</VBtn>
      <VBtn :value="'users'" class="tab-btn">전체 사용자 관리</VBtn>
      <VBtn :value="'sites'" class="tab-btn">전체 기관 관리</VBtn>
    </VBtnToggle>
  </div>

  <!-- 본문 -->
  <VRow class="page-wrap">
    <!-- 좌측: Roles -->
    <VCol cols="12" md="4">
      <VCard elevation="1" class="roles-card">
        <VCardTitle class="pt-4 pb-1 px-4">역할</VCardTitle>
        <VCardText class="py-0 px-2">
          <VList nav :lines="false" class="roles-list">
            <VListItem
              v-for="item in roleData"
              :key="item.name"
              :value="item.name"
              :class="['role-item', { active: currentRole?.name === item.name }]"
              @click="getPermissions(item)"
            >
              <template #prepend>
                <VIcon icon="tabler-point" />
              </template>
              <VListItemTitle>{{ item.realName }}</VListItemTitle>
              <template #append>
                <VChip v-if="currentRole?.name === item.name" color="primary" size="x-small" variant="flat">선택됨</VChip>
              </template>
            </VListItem>
          </VList>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 우측: 권한 패널 -->
    <VCol cols="12" md="8">
      <div class="right-top">
        <h3 class="role-title">{{ currentRole?.realName || '역할 선택' }}</h3>
      </div>

      <VExpansionPanels variant="accordion" class="perm-panels">
        <VExpansionPanel v-for="(menu, i) in menus" :key="i" elevation="0" class="perm-panel">
          <VExpansionPanelTitle>
            <template v-if="!menu.subs" #actions />
            <VRow class="w-100">
              <VCol cols="8" class="panel-title">
                {{ menu.title }}
              </VCol>
              <VCol cols="4" class="panel-control">
                <AppSelect
                  v-if="!menu.subs"
                  :model-value="p[permKey(menu)]"
                  :items="menu.items"
                  placeholder="권한 없음"
                  chips multiple closable-chips
                  @update:model-value="v => onPermChange(menu, v)"
                />
              </VCol>
            </VRow>
          </VExpansionPanelTitle>

          <VExpansionPanelText v-if="menu.subs" class="pt-1">
            <VRow v-for="(sub, j) in menu.subs" :key="j" class="sub-row">
              <VDivider class="my-2" />
              <VCol cols="8" class="sub-title">
                {{ sub.title }}
              </VCol>
              <VCol cols="4" class="panel-control">
                <AppSelect
                  v-model="p[sub.permission]"
                  :items="sub.items"
                  placeholder="권한 없음"
                  chips multiple closable-chips
                />
              </VCol>
            </VRow>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>

      <div class="save-row">
        <VBtn color="primary" class="btn-save" :disabled="!currentRole" @click="save">
          저장
        </VBtn>
      </div>
    </VCol>
  </VRow>
</template>

<style scoped>
/* 헤더 */
.divider-vert{
  background-color: rgba(0,0,0,10%);
  block-size: 30px; inline-size: 1px; margin-inline: 15px;
}
.icon-20{ block-size:20px; inline-size:20px; }
.text-gray-600{ color:#4B5563; font-size:14px; }
.mgmt-quick{ border-radius:6px; cursor:pointer; transition:background-color .2s; }

/* 상단 탭 */
.mgmt-tabs{
  display:flex; justify-content:flex-start; padding-block: 16px 0; padding-inline: 34px;
}

.tab-toggle{
  border: 1px solid #E8ECEF; border-radius: 8px; background:#f4f6fa; box-shadow: 0 3px 8px rgba(201,201,201,25%);
}

.tab-btn{
  min-inline-size: 160px;
}

.tab-btn.v-btn--active{
  background:#eef0ff !important;
}

/* 레이아웃 */
.page-wrap{ margin-block: 14px 28px; margin-inline:34px; }

/* Roles 카드 */
.roles-card{ border-radius: 10px; }

.roles-list :deep(.v-list-item){
  border-radius:10px; margin-block: 2px; margin-inline: 6px;
}
.role-item{ transition: background-color .2s; }
.role-item.active{ background:#eef5ff; }

/* 제목/컨트롤 정렬 */
.right-top{ display:flex; align-items:center; justify-content:space-between; margin-block-end: 8px; }
.role-title{ color:#1f2937; font-weight: 700; margin-block: 6px 12px; margin-inline: 0; }



/* ── 페이지 타이틀 ─────────────────────────────────────── */
.page-title-wrap{ padding-block: 50px 0; padding-inline: 34px; }

.page-title{ margin:0;
  border-block-end: 1px solid #DAE6EE; color:#111827; font-size:22px; font-weight:700;
  margin-block-end : 20px;
  padding-block-end: 20px; }


/* 패널 */
.perm-panels{ --panel-pad: 10px; }

.perm-panel{
  border: 1px solid #eef2f7; border-radius: 10px;
  background: #fff; margin-block-end: 10px;
}
.panel-title{ display:flex; align-items:center; color:#1f2937; font-weight:600; }
.panel-control :deep(.v-field){ border-radius:10px; }

/* 서브 행 */
.sub-row{ align-items:center; }
.sub-title{ font-size: 15px; padding-inline-start: 24px; }

/* 저장 버튼 */
.save-row{ display:flex; justify-content:flex-end; padding-block-start: 6px; }
.btn-save{ min-inline-size: 120px; }


.v-data-table .v-table__wrapper > table > thead > tr > th,
.v-data-table .v-table__wrapper > table > tbody > tr > th {
  font-size: 15px;
}



:deep(.custom-table .v-table__wrapper table) {
  th {
    background: #F3F5F8 !important;  /* 밝은 회색 배경 */
    border-block-end: 1px solid #eee;
    color: #909090 !important;       /* 연한 회색 텍스트 */
    font-size: 13px;
    font-weight: 500;
    padding-block: 10px;
    padding-inline: 12px;
  }

  td {
    border-block-end: 1px solid #E8EBEE;
    color: #444;
    font-size: 14px;
    padding-block: 8px;
    padding-inline: 12px;
  }

  tr:hover td {
    background-color: #f9f9fc; /* hover 시 은은한 하이라이트 */
  }
}

:deep(.custom-table .v-badge__badge) {
  border-radius: unset;
  font-size: 8px;
  margin-inline-start: 5px;
  min-inline-size: 10px;
}

:deep(.v-card-item) {
  padding: 12px;
}
</style>
